import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ds";
import { readBlobText } from "@/lib/splash/checkSplashFile";
import type { AppSplashMediaType } from "@/types/generated/cms.generated";

/** What to play: a local file before upload, or the stored file through its public URL. */
export type SplashPreviewSource =
  | { kind: "file"; file: File; mediaType: AppSplashMediaType; title: string }
  | { kind: "url"; url: string; mediaType: AppSplashMediaType; title: string; fileName: string };

interface SplashPreviewProps {
  source: SplashPreviewSource | null;
  onClose: () => void;
  /** Hands focus back to the control that opened the preview (it is not a Radix Trigger). */
  onCloseAutoFocus?: (event: Event) => void;
}

/** The file part of a blob URL, e.g. ".../app-splashes/0193….mp4" ⇒ "0193….mp4". */
export function splashFileName(url: string | null | undefined): string {
  if (!url) return "";
  const path = url.split(/[?#]/)[0];
  return decodeURIComponent(path.slice(path.lastIndexOf("/") + 1));
}

async function readAnimationData(source: SplashPreviewSource): Promise<unknown> {
  if (source.kind === "file") return JSON.parse(await readBlobText(source.file));
  // Storage allows GET from the CMS origin (the same CORS the SAS PUT already relies on).
  const response = await fetch(source.url);
  if (!response.ok) throw new Error(response.statusText);
  return response.json();
}

/** Plays a Lottie once with lottie-web, loaded only here (dynamic import, light SVG build). */
function LottiePlayer({ source, replay }: { source: SplashPreviewSource; replay: number }) {
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let destroy: (() => void) | undefined;
    setFailed(false);
    (async () => {
      try {
        const [{ default: lottie }, animationData] = await Promise.all([
          import("lottie-web/build/player/lottie_light"),
          readAnimationData(source),
        ]);
        if (cancelled || !container.current) return;
        const animation = lottie.loadAnimation({
          container: container.current,
          renderer: "svg",
          loop: false,
          autoplay: true,
          animationData,
        });
        destroy = () => animation.destroy();
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      destroy?.();
    };
  }, [source, replay]);

  if (failed) {
    const name = source.kind === "file" ? source.file.name : source.fileName;
    return (
      <p className="p-3 text-center text-[12.5px] text-ds-navink" data-testid="splash-preview-fallback">
        This animation can't be played here. File: {name}
      </p>
    );
  }
  return <div ref={container} className="h-full w-full" data-testid="splash-preview-lottie" />;
}

function VideoPlayer({ source, replay }: { source: SplashPreviewSource; replay: number }) {
  const [url, setUrl] = useState<string | null>(source.kind === "url" ? source.url : null);

  useEffect(() => {
    if (source.kind === "url") {
      setUrl(source.url);
      return;
    }
    const objectUrl = URL.createObjectURL(source.file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [source]);

  if (!url) return null;
  // Muted, plays once (no loop), the way phones play it. `key` restarts it on Play again.
  return (
    <video
      key={replay}
      src={url}
      muted
      autoPlay
      playsInline
      className="h-full w-full object-cover"
      data-testid="splash-preview-video"
    />
  );
}

/** Preview dialog: the splash in a 9:16 frame, played once (MP4 muted; Lottie via lottie-web). */
export function SplashPreview({ source, onClose, onCloseAutoFocus }: SplashPreviewProps) {
  const [replay, setReplay] = useState(0);

  return (
    <DialogPrimitive.Root open={source !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ds-nav/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          className="fixed left-1/2 top-1/2 z-50 flex w-[min(360px,calc(100%-32px))] -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-ds-lg border border-ds-line bg-ds-panel p-4 font-ds text-ds-ink shadow-ds"
          data-testid="splash-preview"
        >
          <div>
            <DialogPrimitive.Title className="text-[15px] font-semibold">{source?.title ?? "Preview"}</DialogPrimitive.Title>
            <p className="text-[13px] text-ds-ink2">Plays once, the way phones play it.</p>
          </div>
          <div className="mx-auto grid aspect-[9/16] w-[200px] place-items-center overflow-hidden rounded-ds border-4 border-ds-ink bg-ds-nav">
            {source &&
              (source.mediaType === "Lottie" ? (
                <LottiePlayer source={source} replay={replay} />
              ) : (
                <VideoPlayer source={source} replay={replay} />
              ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReplay((n) => n + 1)}>
              Play again
            </Button>
            <DialogPrimitive.Close asChild>
              <Button variant="primary">Close</Button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
